'use client';
import type React from 'react';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useSmartIntakeCtx, setKnownCity, resolvePageCity } from '@/lib/smartIntakeStore';
import { findCity } from '@/lib/israelCities';
import SearchSuggest from './SearchSuggest';
import ReviewsV2Word from './ReviewsV2Word';
import { avatarImg } from '@/lib/imgUrl';

const PAGES: [string, string][] = [
  ['🔎 קטגוריות', '/קטגוריה'],
  ['📚 מדריכים ומאמרים', '/מאמרים'],
  ['ℹ️ אודות', '/אודות'],
  ['✉️ צור קשר', '/צור-קשר'],
];
// Founder 2026-09-28: one bottom line — "כניסת בעלי עסקים · פרטיות · תנאי שימוש" (was 3 full rows), so the menu fits one screen
const LEGAL: [string, string][] = [
  ['🔐 כניסת בעלי עסקים', '/כניסת-בעלי-עסקים'], // phone → magic link on WhatsApp
  ['פרטיות', '/פרטיות'],
  ['תנאי שימוש', '/תנאים'],
];

// "למה אנחנו?" — 3D transparent icons (scripts/pricing-icons.mjs, prefix why-) in a 3×2 box grid, Founder 2026-09-27.
const WHY: { icon: string; label: React.ReactNode }[] = [
  { icon: 'why-objective', label: <><ReviewsV2Word /> אובייקטיביות בלבד</> }, // המלצות on a reviews-v2 preview page
  { icon: 'why-verified', label: <><ReviewsV2Word /> מאומתות בלבד</> },
  { icon: 'why-free', label: 'זה בחינם' },
  { icon: 'why-fast', label: 'תגובה מהירה' },
  { icon: 'why-love-red', label: 'נעשה באהבה' }, // red 3D heart (Founder 2026-09-28)
  // r8b: the 9+ line removed pending Founder wording (raw-rating law). Prepared: { icon: 'home-top9', label: <>רק מומחים עם דירוג <bdi dir="ltr">4.5+</bdi> (מתוך 5)</> }
];

export default function MobileMenu() {
  const [open, setOpen] = useState(false);
  // Escape closes the drawer (bughunter a11y 2026-09-27)
  useEffect(() => { if (!open) return; const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [open]);
  const [prof, setProf] = useState('');
  const [city, setCity] = useState('');
  const router = useRouter();
  // the menu's city field listens to the shared city (Founder 2026-09-28): a city known anywhere is already filled in here
  const intake = useSmartIntakeCtx();
  // 2026-09-28 (Founder: sync the menu with the questionnaire / page city and the category): every time the menu OPENS it
  // takes the page's current city (lib/smartIntakeStore resolvePageCity — the same rule the list, the badge and the WhatsApp
  // button use) and, on a category page, the category — it used to fill the city only while empty, so a city changed later on
  // the page (ירושלים) still showed the old one (אשדוד).
  useEffect(() => {
    if (!open) return;
    setCity(resolvePageCity(intake) || intake?.city || '');
    const catName = (document.querySelector('[data-page-category-name]') as HTMLElement | null)?.dataset.pageCategoryName;
    if (catName) setProf(catName);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function go(profV: string, cityV: string) {
    const params = new URLSearchParams();
    if (profV.trim()) params.set('q', profV.trim());
    if (cityV.trim()) params.set('city', cityV.trim());
    const known = cityV.trim() ? findCity(cityV.trim()) : null; // …and a city typed here becomes the visitor's city everywhere
    if (known) setKnownCity(known.name, { lat: known.lat, lng: known.lng });
    setOpen(false);
    window.location.href = params.toString() ? `/search?${params.toString()}` : '/'; // full navigation → the resolver's redirect puts the real category/city URL in the address bar (2026-09-27)
  }
  function submitSearch(e: React.FormEvent) { e.preventDefault(); go(prof, city); }
  // 2026-09-28 (Founder): a pick completes the pair → search at once; a missing half → the cursor goes there.
  const focusField = (sel: string) => setTimeout(() => (document.querySelector(`${sel} input`) as HTMLInputElement | null)?.focus(), 30);
  function pickedProf(name: string) { if (findCity(city.trim())) go(name, city); else focusField('[data-mm-city]'); }
  function pickedCity(name: string) { if (prof.trim()) go(prof, name); else focusField('[data-mm-prof]'); }

  return (
    <>
      {/* Hamburger button — white stripes, on the right (start in RTL) */}
      <button onClick={() => setOpen(true)} aria-label="תפריט" aria-expanded={open}
        className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col gap-[5px] p-2 rounded-lg"
        style={{ background: 'rgba(255,255,255,.12)' }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{ display: 'block', width: 22, height: 2.5, borderRadius: 2, background: '#fff' }} />
        ))}
      </button>

      {open && (
        <div className="fixed inset-0 z-[9998]" dir="rtl">
          {/* Backdrop */}
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,.45)' }} onClick={() => setOpen(false)} />

          {/* Panel — slides in from the right */}
          <div className="absolute top-0 right-0 h-full w-[86%] max-w-sm overflow-y-auto flex flex-col"
            style={{ background: 'var(--sk-drawer, #fff)', boxShadow: '-8px 0 30px rgba(0,0,0,.25)' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4"
              style={{ background: 'var(--sk-band, linear-gradient(135deg,#0d47a1,#1976d2))' }}>
              <picture>
                <source srcSet="/brand/logo-white.avif" type="image/avif" />
                <source srcSet="/brand/logo-white.webp" type="image/webp" />
                <img src="/brand/logo-white.webp" alt="המומחים" width={227} height={38} className="h-[26px] w-auto" style={{ objectFit: 'contain' }} />
              </picture>
              <button onClick={() => setOpen(false)} aria-label="סגור"
                className="text-white text-2xl leading-none font-bold">✕</button>
            </div>

            <div className="px-5 pt-4 pb-3 space-y-4 flex-1 flex flex-col">
              {/* Search */}
              <form onSubmit={submitSearch}>
                <p className="text-sm font-black mb-2 text-center" style={{ color: 'var(--sk-kicker, #0f172a)' }}>🔍 חיפוש מומחה</p>
                {/* live suggestions (2026-09-27): category + city while typing, typos included */}
                {/* 2026-09-28: the two fields side by side (the menu should fit one screen) */}
                <div className="mb-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, position: 'relative' }}>
                  <div data-mm-prof><SearchSuggest type="cat" value={prof} onChange={setProf} onPick={it => pickedProf(it.name)}
                    placeholder="בעל מקצוע" inputClass="w-full px-3 py-2.5 rounded-xl text-sm outline-none" inputStyle={{ border: '1.5px solid #e2e8f0' }} wideDropdown /></div>
                  <div data-mm-city><SearchSuggest type="city" value={city} onChange={setCity} onPick={it => pickedCity(it.name)}
                    placeholder="עיר" inputClass="w-full px-3 py-2.5 rounded-xl text-sm outline-none" inputStyle={{ border: '1.5px solid #e2e8f0' }} wideDropdown /></div>
                </div>
                <button type="submit"
                  className="w-full py-2.5 rounded-xl text-sm font-bold text-white"
                  style={{ background: 'var(--sk-accent, #0d47a1)', color: 'var(--sk-on-accent, #fff)' }}>
                  חפש
                </button>
              </form>

              {/* Pages */}
              <div style={{ borderTop: '1px solid var(--sk-line2, #f1f5f9)', paddingTop: 12 }}>
                <p className="text-sm font-black mb-2 text-center" style={{ color: 'var(--sk-kicker, #0f172a)' }}>תפריט</p>
                {/* Founder 2026-09-28: two columns, text centred — saves the vertical space of 4 full-width rows */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {PAGES.map(([label, href]) => (
                    <Link key={href} href={href} onClick={() => setOpen(false)}
                      className="text-sm font-bold flex items-center justify-center text-center min-h-[48px] rounded-xl"
                      style={{ color: 'var(--sk-head, #0f172a)', background: 'var(--sk-glass, #f8fafc)', border: '1.5px solid var(--sk-line, #e2e8f0)', padding: '6px 8px', lineHeight: 1.25 }}>
                      {label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Why us */}
              <div style={{ borderTop: '1px solid var(--sk-line2, #f1f5f9)', paddingTop: 12 }}>
                <p className="text-sm font-black mb-2 text-center" style={{ color: 'var(--sk-kicker, #0f172a)' }}>למה אנחנו?</p>
                <ul style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, listStyle: 'none', margin: 0, padding: 0 }}>
                  {WHY.map(w => (
                    <li key={w.icon} style={{ background: 'var(--sk-glass, #fff)', border: '1.5px solid var(--sk-line, #e0f2fe)', borderRadius: 12, padding: '6px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, textAlign: 'center', boxShadow: '0 1px 6px rgba(13,71,161,.05)' }}>
                      <img {...avatarImg(`/img/pricing/${w.icon}.webp`, 34)} alt="" aria-hidden width={34} height={34} loading="lazy" decoding="async" style={{ width: 34, height: 34, objectFit: 'contain' }} />
                      <span style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--sk-head, #0f172a)', lineHeight: 1.2 }}>{w.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* One bottom line (Founder 2026-09-28): כניסת בעלי עסקים · פרטיות · תנאי שימוש */}
              <nav aria-label="קישורים נוספים" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', borderTop: '1px solid var(--sk-line2, #f1f5f9)', paddingTop: 6, marginTop: 'auto' /* pinned to the bottom of the menu */ }}>
                {LEGAL.map(([label, href], i) => (
                  <span key={href} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {i > 0 && <span aria-hidden style={{ color: 'var(--sk-muted, #cbd5e1)', padding: '0 2px' }}>·</span>}
                    <Link href={href} onClick={() => setOpen(false)} className="text-[13px] min-h-[44px] inline-flex items-center"
                      style={{ color: i === 0 ? 'var(--sk-link, #0d47a1)' : 'var(--sk-muted, #64748b)', fontWeight: i === 0 ? 800 : 600, padding: '0 6px', whiteSpace: 'nowrap' }}>
                      {label}
                    </Link>
                  </span>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
